/*
  WONDR/POWR product card (redesign) — gedrag voor snippets/powr-card.liquid.

  - "In winkelmand": PBCart.addLine, knop 2s naar ✓, toast 4s met "Bekijk".
    Figma board P asks for no modal/drawer that interrupts scrolling. While
    this card adds, PBCart's cart:open is stopped and pb:cart:updated keeps its
    source 'add' (Converge "Added To Cart" relies on it) but
    gets detail.pbSuppressDrawer, which assets/cart-drawer-toggle.js reads to
    skip its automatic open. Counter and drawer contents still update.
    With data-after-add="drawer" on an ancestor the old behaviour stays (drawer opens).
    One add per button at a time (data-pending), also for keyboard activation.
    The button is a submit button in a form[action="/cart/add"] so Dalton's
    add-to-cart listener counts it; preventDefault keeps the form from submitting.
  - "Bundel & bespaar": pushes { event: 'pb_pick_mix_click' } to the
    dataLayer and then follows the link as usual. The Dalton SDK has no
    dalton.fn.event; it intercepts dataLayer events whose name is a configured
    goal (Dalton settings) and records them as ga_pb_pick_mix_click, flushed
    with keepalive on pagehide.
  - Memberregel: opent de member-sheet met de prijzen van die card.
  - Sheet en toast krijgen data-brand van de card waarop getikt werd, zodat
    ze de kleuren en fonts van dat merk dragen (ook op planetb.care, waar
    WONDR- en POWR-cards door elkaar staan).
  - Rating: live from Reviews.io (public rating-batch endpoint, store id only),
    one request per 100 SKUs for every card on the page. A bundle card lists the
    SKUs of its component products, so its score is their combined score. The
    server-rendered rating stays when the API fails or has no reviews.

  Event delegation op document, zodat cards die via infinite scroll binnenkomen
  meteen werken. New cards also get their live rating (MutationObserver).
*/
(() => {
  if (window.__powrCardInit) return;
  window.__powrCardInit = true;

  // Member-sheet en toast staan in een <template> uit snippets/powr-card-global.liquid.
  function mountGlobals() {
    const template = document.getElementById('PowrCardGlobals');
    if (!template || document.querySelector('[data-powr-sheet]')) return;
    document.body.append(template.content.cloneNode(true));
  }

  // One Reviews.io store serves every brand.
  const REVIEWS_STORE = 'planetb.care';
  const REVIEWS_BATCH = 100;
  // Requested SKU -> Reviews.io rows. Reviews.io groups variant SKUs: a row can have another sku than the one asked
  // for, and its lookup lists the group, so a row is stored under every SKU of its group.
  const ratingRows = new Map();

  async function fetchRatingRows(skus) {
    const missing = skus.filter((sku) => !ratingRows.has(sku));
    const chunks = [];
    for (let i = 0; i < missing.length; i += REVIEWS_BATCH) chunks.push(missing.slice(i, i + REVIEWS_BATCH));
    await Promise.all(
      chunks.map(async (chunk) => {
        const url = new URL('https://api.reviews.io/product/rating-batch');
        url.searchParams.set('store', REVIEWS_STORE);
        url.searchParams.set('sku', chunk.join(';'));
        // A string, not the URL object: a site-wide fetch wrapper calls url.includes() and throws on a URL object.
        const response = await fetch(url.toString());
        if (!response.ok) throw new Error(`Reviews.io ${response.status}`);
        const rows = await response.json();
        chunk.forEach((sku) => ratingRows.set(sku, []));
        rows.forEach((row) => {
          String(row.lookup || '')
            .split(';')
            .concat(row.sku)
            .forEach((sku) => ratingRows.get(sku)?.push(row));
        });
      }),
    );
  }

  // Shopify Bundles: Liquid cannot read their components, so a card without SKUs (data-review-product) gets the
  // component SKUs from the Storefront API, with the theme's public config (same as assets/bundle-included-items.js).
  async function resolveBundleSkus(els) {
    const cfg = window.PB_UNIFIED_CART || {};
    const handles = [...new Set(els.map((el) => el.dataset.reviewProduct).filter(Boolean))];
    if (!handles.length || !cfg.endpoint || !cfg.token) return;
    const params = handles.map((_, i) => `$h${i}: String!`).join(', ');
    const fields = handles
      .map(
        (_, i) =>
          `p${i}: product(handle: $h${i}) { variants(first: 10) { nodes { components(first: 30) { nodes { productVariant { sku } } } } } }`,
      )
      .join(' ');
    const response = await fetch(cfg.endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': cfg.token },
      body: JSON.stringify({
        query: `query(${params}) { ${fields} }`,
        variables: Object.fromEntries(handles.map((handle, i) => [`h${i}`, handle])),
      }),
    });
    const { data } = await response.json();
    handles.forEach((handle, i) => {
      const skus = (data?.[`p${i}`]?.variants.nodes || []).flatMap((variant) =>
        variant.components.nodes.map((component) => component.productVariant?.sku),
      );
      const joined = [...new Set(skus.filter(Boolean))].join(';');
      els.filter((el) => el.dataset.reviewProduct === handle).forEach((el) => (el.dataset.reviewSkus = joined));
    });
  }

  function renderRating(el) {
    const rows = new Map();
    el.dataset.reviewSkus.split(';').forEach((sku) => {
      (ratingRows.get(sku) || []).forEach((row) => rows.set(row.sku, row));
    });
    let count = 0;
    let total = 0;
    rows.forEach((row) => {
      const n = Number(row.num_ratings) || 0;
      count += n;
      total += n * (Number(row.average_rating) || 0);
    });
    if (count === 0) {
      // A Shopify Bundles card starts with an empty slot; without reviews it gets the same badge as any product.
      if (el.dataset.reviewProduct && !el.childElementCount) {
        const badge = document.createElement('span');
        badge.className = 'powr-card__badge-new';
        badge.textContent = 'Nieuw';
        el.append(badge);
      }
      return;
    }

    const average = total / count;
    // Same label as the Liquid: two decimals, a round score as "5,0".
    let label = String(Number(average.toFixed(2)));
    if (!label.includes('.')) label += '.0';
    label = label.replace('.', ',');

    const stars = document.createElement('span');
    stars.className = 'powr-card__stars';
    stars.style.setProperty('--powr-rating', `${Math.round((average / 5) * 1000) / 10}%`);
    stars.setAttribute('role', 'img');
    stars.setAttribute('aria-label', `${label} van 5 sterren, ${count} reviews`);
    const score = document.createElement('span');
    score.className = 'powr-card__rating-score';
    score.setAttribute('aria-hidden', 'true');
    score.textContent = label;
    const counter = document.createElement('span');
    counter.className = 'powr-card__rating-count';
    counter.setAttribute('aria-hidden', 'true');
    counter.textContent = `(${count})`;
    el.replaceChildren(stars, score, counter);
  }

  let ratingsQueued = false;
  function queueRatings() {
    if (ratingsQueued) return;
    ratingsQueued = true;
    setTimeout(async () => {
      ratingsQueued = false;
      const els = [...document.querySelectorAll('.powr-card__rating[data-review-skus]:not([data-rating-live])')];
      if (!els.length) return;
      els.forEach((el) => (el.dataset.ratingLive = ''));
      try {
        await resolveBundleSkus(els.filter((el) => !el.dataset.reviewSkus));
      } catch {
        /* those bundle cards keep their empty slot; the other cards still load */
      }
      const skus = [...new Set(els.flatMap((el) => el.dataset.reviewSkus.split(';')).filter(Boolean))];
      try {
        await fetchRatingRows(skus);
        els.forEach(renderRating);
      } catch {
        /* keep the server-rendered rating */
      }
    }, 50);
  }

  function init() {
    mountGlobals();
    queueRatings();
    new MutationObserver(queueRatings).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  const ADDED_MS = 2000;
  const TOAST_MS = 4000;

  let suppressDrawer = 0;

  document.addEventListener(
    'cart:open',
    (event) => {
      if (suppressDrawer > 0) event.stopImmediatePropagation();
    },
    true,
  );

  // Flag only: the event itself (and its source) must reach every listener.
  document.addEventListener(
    'pb:cart:updated',
    (event) => {
      if (suppressDrawer === 0 || !event.detail) return;
      const source = event.detail.source;
      if (source !== 'add' && source !== 'addLine') return;
      event.detail.pbSuppressDrawer = true;
    },
    true,
  );

  async function waitForPBCart(tries = 80, delay = 50) {
    for (let i = 0; i < tries; i++) {
      if (window.PBCart && typeof window.PBCart.addLine === 'function') return window.PBCart;
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
    throw new Error('PBCart.addLine is niet beschikbaar');
  }

  function openDrawer() {
    if (window.PBCartDrawer?.open) {
      window.PBCartDrawer.open();
    } else {
      document.dispatchEvent(new CustomEvent('cart:open'));
    }
  }

  function brandOf(element) {
    return element.closest('[data-card-brand]')?.dataset.cardBrand || 'powr';
  }

  let toastTimer;

  function showToast(imageUrl, brand) {
    const toast = document.querySelector('[data-powr-toast]');
    if (!toast) return;
    toast.dataset.brand = brand;
    const thumb = toast.querySelector('[data-powr-toast-thumb]');
    if (thumb) {
      if (imageUrl) {
        thumb.src = imageUrl;
        thumb.hidden = false;
      } else {
        thumb.hidden = true;
      }
    }
    clearTimeout(toastTimer);
    toast.classList.remove('is-leaving');
    toast.hidden = false;
    toastTimer = setTimeout(() => {
      toast.classList.add('is-leaving');
      toastTimer = setTimeout(() => {
        toast.hidden = true;
        toast.classList.remove('is-leaving');
      }, 200);
    }, TOAST_MS);
  }

  function hideToast() {
    const toast = document.querySelector('[data-powr-toast]');
    if (!toast) return;
    clearTimeout(toastTimer);
    toast.hidden = true;
  }

  async function addToCart(button) {
    const variantId = button.dataset.variantId;
    if (!variantId || button.disabled || button.dataset.pending === 'true') return;

    const keepDrawer = button.closest('[data-after-add="drawer"]') !== null;

    // Not `disabled`: that would drop keyboard focus from the button.
    button.dataset.pending = 'true';
    button.setAttribute('aria-disabled', 'true');
    button.classList.add('is-loading');
    button.setAttribute('aria-busy', 'true');

    try {
      const cart = await waitForPBCart();
      if (!keepDrawer) suppressDrawer++;
      try {
        await cart.addLine(variantId, 1);
      } finally {
        if (!keepDrawer) {
          // pb-cart.js opent de drawer ook nog 60ms na pb:cart:updated.
          setTimeout(() => {
            suppressDrawer = Math.max(0, suppressDrawer - 1);
          }, 150);
        }
      }

      button.classList.remove('is-loading');
      button.classList.add('is-added');
      button.setAttribute('aria-label', 'Toegevoegd aan winkelmand');
      setTimeout(() => {
        button.classList.remove('is-added');
        button.removeAttribute('aria-label');
      }, ADDED_MS);

      if (!keepDrawer) showToast(button.dataset.image, brandOf(button));
    } catch (error) {
      console.error('[Product card] toevoegen mislukt', error);
      button.classList.remove('is-loading');
      window.alert('Toevoegen aan je winkelmand lukte niet. Probeer het opnieuw.');
    } finally {
      delete button.dataset.pending;
      button.removeAttribute('aria-disabled');
      button.removeAttribute('aria-busy');
    }
  }

  function openMemberSheet(trigger) {
    const sheet = document.querySelector('[data-powr-sheet]');
    if (!sheet || typeof sheet.showModal !== 'function') return;

    const fill = (selector, value) => {
      const el = sheet.querySelector(selector);
      if (el) el.textContent = value || '';
    };
    fill('[data-powr-sheet-member]', trigger.dataset.memberPrice);
    fill('[data-powr-sheet-product]', trigger.dataset.productLabel);
    fill('[data-powr-sheet-discount]', trigger.dataset.discount);
    fill('[data-powr-sheet-price]', trigger.dataset.price);
    sheet.dataset.brand = brandOf(trigger);

    sheet.showModal();
  }

  function trackPickMixClick(link) {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'pb_pick_mix_click',
        brand: brandOf(link),
        product_id: link.closest('[data-product-id]')?.dataset.productId || '',
      });
    } catch {
      /* tracking must never block the link */
    }
  }

  document.addEventListener('click', (event) => {
    const pickMix = event.target.closest('[data-powr-pickmix]');
    if (pickMix) {
      trackPickMixClick(pickMix);
      return;
    }

    const atc = event.target.closest('[data-powr-atc]');
    if (atc) {
      event.preventDefault();
      event.stopPropagation();
      addToCart(atc);
      return;
    }

    // "Houd mij op de hoogte": normaal vangt de body-listener uit
    // snippets/add-to-cart-button.liquid (.bis-collection-btn) dit al op en
    // stopt hij de propagatie. Komt de klik toch hier, dan openen we hetzelfde
    // Klaviyo-formulier, zodat de knop nooit dood is.
    const notify = event.target.closest('[data-powr-bis]');
    if (notify) {
      event.preventDefault();
      window._klOnsite = window._klOnsite || [];
      window._klOnsite.push(['openForm', 'W2Syx5']);
      return;
    }

    const member = event.target.closest('[data-powr-member]');
    if (member) {
      event.preventDefault();
      event.stopPropagation();
      openMemberSheet(member);
      return;
    }

    if (event.target.closest('[data-powr-sheet-close]')) {
      event.target.closest('dialog')?.close();
      return;
    }

    // Klik op de backdrop van de sheet sluit hem.
    if (event.target.matches?.('[data-powr-sheet]')) {
      event.target.close();
      return;
    }

    if (event.target.closest('[data-powr-toast-open]')) {
      hideToast();
      openDrawer();
    }
  });
})();
